import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Terminal, Image as ImageIcon, FileText, ArrowRightLeft, Calculator } from 'lucide-react';
import { motion } from 'framer-motion';

export default function Hero() {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  // Floating shape variants for framer-motion
  const floatAnimation = (delay: number) => ({
    animate: {
      y: [0, -10, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut",
        delay: delay
      }
    }
  });

  return (
    <section className="relative py-16 md:py-24 px-6 overflow-hidden bg-white border-b border-slate-50 theme-transition">
      {/* Background soft decoration */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent pointer-events-none" />
      
      <div className="max-w-[1240px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        {/* Left Column: Heading, Subheading, Search, CTAs */}
        <div className="lg:col-span-6 space-y-8 text-left">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/5 rounded-full border border-primary/10">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary/40 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
            </span>
            <span className="text-[10px] md:text-xs font-bold text-primary tracking-wider uppercase">
              100% Free & Browser-Based
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-[54px] font-bold text-slate-900 tracking-tight leading-[1.15]">
            We make online <br className="hidden md:inline" />
            tools <span className="text-primary">easy</span>.
          </h1>

          <p className="text-base md:text-lg text-slate-500 font-medium leading-relaxed max-w-xl">
            All the browser utilities you need to format, convert, and calculate. Fast, secure, and 100% client-side—your data never leaves your device.
          </p>

          {/* Search Box */}
          <form onSubmit={handleSearchSubmit} className="relative max-w-lg">
            <div 
              className="relative flex items-center bg-slate-50 rounded-xl shadow-sm focus-within:shadow-md focus-within:bg-white transition-all duration-300 overflow-hidden"
              style={{ border: 'none', outline: 'none' }}
            >
              <div className="pl-4 text-slate-400">
                <Search size={18} />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search 1,700+ free online tools (e.g. JSON, Celsius)..."
                className="w-full bg-transparent pl-3 pr-4 py-3.5 text-sm font-semibold text-slate-800 placeholder:text-slate-400 focus:outline-none"
                style={{ border: 'none', outline: 'none', boxShadow: 'none' }}
              />
              <button
                type="submit"
                className="px-5 py-3.5 bg-primary hover:bg-primary/95 text-white font-bold text-xs uppercase tracking-wider transition-colors flex-shrink-0"
                style={{ border: 'none' }}
              >
                Search
              </button>
            </div>
          </form>

          {/* CTAs */}
          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={() => navigate('/all-tools')}
              className="px-6 py-3 bg-primary hover:bg-primary/95 text-white font-bold rounded-lg text-sm shadow-sm active:scale-95 transition-all"
            >
              Explore All Tools
            </button>
            <a
              href="#categories"
              className="px-6 py-3 border border-primary text-primary hover:bg-primary/5 font-bold rounded-lg text-sm transition-all shadow-sm"
            >
              Browse Categories
            </a>
          </div>
        </div>

        {/* Right Column: Premium CSS-based Mockup */}
        <div className="lg:col-span-6 flex justify-center items-center relative mt-8 lg:mt-0">
          <div className="relative w-full max-w-[440px] aspect-[4/3] flex items-center justify-center">
            
            {/* Background elements */}
            <div className="absolute w-72 h-72 bg-primary/5 rounded-full blur-3xl -z-10" />

            {/* Central Mockup Browser Card */}
            <div className="relative w-[340px] h-[220px] bg-white border border-slate-200/60 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.08)] flex flex-col overflow-hidden z-10">
              {/* Browser bar */}
              <div className="h-8 bg-slate-50/80 border-b border-slate-100 flex items-center px-4 gap-1.5 flex-shrink-0">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F56]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#27C93F]" />
                <div className="h-4 w-32 bg-slate-200/50 rounded ml-4" />
              </div>
              
              {/* Document Canvas Content */}
              <div className="flex-grow p-6 flex flex-col gap-2.5 relative justify-center">
                {/* Simulated text lines */}
                <div className="space-y-2">
                  <div className="h-2 w-3/4 bg-slate-100 rounded" />
                  <div className="h-2 w-full bg-slate-100 rounded" />
                  <div className="h-2 w-5/6 bg-slate-100 rounded" />
                  <div className="h-2 w-2/3 bg-slate-100 rounded" />
                </div>

                {/* Selected Blue Circle */}
                <div className="absolute top-1/2 left-1/3 -translate-y-1/2 -translate-x-1/2 w-8 h-8 rounded-full border-2 border-primary bg-primary/10 flex items-center justify-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                </div>

                {/* Simulated Signature Draw (Red hand-drawn signature effect) */}
                <svg className="absolute bottom-6 right-8 w-24 h-12 text-[#FF3B30] opacity-80" viewBox="0 0 100 50" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M 10 35 Q 25 15, 35 25 T 60 15 T 85 25" />
                </svg>

                {/* Simulated Cursor */}
                <svg className="absolute bottom-4 right-24 w-5 h-5 text-slate-800 drop-shadow-md" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M4.5 2.5v19l5.3-5.3h7.7L4.5 2.5z" />
                </svg>
              </div>
            </div>

            {/* Floating Badges */}
            
            {/* 1. Dev Tools (Orange) */}
            <motion.div
              variants={floatAnimation(0.2)}
              animate="animate"
              className="absolute -top-4 -left-4 w-12 h-12 rounded-xl bg-orange-500 text-white flex items-center justify-center shadow-lg shadow-orange-500/20 z-20 cursor-pointer"
              onClick={() => navigate('/category/dev-tools')}
              title="Developer Tools"
            >
              <Terminal size={20} />
            </motion.div>

            {/* 2. Image Tools (Emerald) */}
            <motion.div
              variants={floatAnimation(0.8)}
              animate="animate"
              className="absolute top-24 -right-8 w-12 h-12 rounded-xl bg-emerald-500 text-white flex items-center justify-center shadow-lg shadow-emerald-500/20 z-20 cursor-pointer"
              onClick={() => navigate('/category/image-tools')}
              title="Image Tools"
            >
              <ImageIcon size={20} />
            </motion.div>

            {/* 3. PDF Tools (Rose) */}
            <motion.div
              variants={floatAnimation(1.4)}
              animate="animate"
              className="absolute -bottom-6 left-12 w-12 h-12 rounded-xl bg-rose-500 text-white flex items-center justify-center shadow-lg shadow-rose-500/20 z-20 cursor-pointer"
              onClick={() => navigate('/category/pdf-tools')}
              title="PDF Tools"
            >
              <FileText size={20} />
            </motion.div>

            {/* 4. Converters (Indigo) */}
            <motion.div
              variants={floatAnimation(2.0)}
              animate="animate"
              className="absolute bottom-16 -right-6 w-12 h-12 rounded-xl bg-indigo-500 text-white flex items-center justify-center shadow-lg shadow-indigo-500/20 z-20 cursor-pointer"
              onClick={() => navigate('/category/converters-tools')}
              title="Converters"
            >
              <ArrowRightLeft size={20} />
            </motion.div>

            {/* 5. Calculators (Teal) */}
            <motion.div
              variants={floatAnimation(2.6)}
              animate="animate"
              className="absolute -top-10 right-16 w-12 h-12 rounded-xl bg-teal-500 text-white flex items-center justify-center shadow-lg shadow-teal-500/20 z-20 cursor-pointer"
              onClick={() => navigate('/category/calculator-tools')}
              title="Calculators"
            >
              <Calculator size={20} />
            </motion.div>

            {/* Floating decorative math/code symbols (Smallpdf-style details) */}
            
            {/* Plus sign */}
            <div className="absolute top-10 left-20 text-yellow-400 font-bold text-xl select-none animate-pulse">
              +
            </div>
            
            {/* Asterisk */}
            <div className="absolute bottom-8 right-24 text-sky-400 font-bold text-2xl select-none animate-pulse delay-500">
              *
            </div>

            {/* Cross */}
            <div className="absolute top-36 left-0 text-pink-400 font-bold text-lg select-none rotate-45 animate-pulse delay-1000">
              +
            </div>

            {/* Circle */}
            <div className="absolute bottom-24 left-36 w-3 h-3 border-2 border-emerald-400 rounded-full select-none animate-ping" />
            
            {/* Hash */}
            <div className="absolute -top-6 right-36 text-indigo-400 font-bold text-base select-none animate-pulse delay-700">
              #
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
